import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import IDCapture from "../components/IDCapture";
import KYCLanding from "../components/KYCLanding";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { Box } from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import { fetchCustomerByuuid, uploadFileService } from "../service";
import MobileOnly from "../components/MobileOnly";

export const CAPTURETYPE = {
  LandingPage: 0,
  IDFront: 1,
  IDBack: 2,
  Selfie: 3,
  CaptureSSN: 4,
  CaptureComplete: 5,
};

function MobileVerificationLandingPage(props) {
  const { uuid: customerUuid } = useParams();
  const [currentPageMode, setCurrentPageMode] = useState(CAPTURETYPE.LandingPage);
  const [fileUploadinProgress, setFileUploadInProgress] = useState(false);
  const [fileUploadFailed, setFileUploadFailed] = useState(false);
  const [customerValidated, setCustomerValidated] = useState(false);
  const customerValid = useRef(false);
  const ssnRequired = useRef(false);
  const customerPhoneNumber = useRef("");

  useEffect(() => {
    const validateCustomer = async () => {
      try {
        var response = await fetchCustomerByuuid(customerUuid);
  
        const val = await response.json();
        if (val?.uuid) {
          if (
            val?.ssn_required &&
            val?.ssn_required != null &&
            val?.ssn_required !== false
          ) {
            ssnRequired.current = true;
          } else {
            ssnRequired.current = false;
          }
          customerPhoneNumber.current = val?.customer_phone_number;
          customerValid.current = true;
        } else {
          customerValid.current = false;
        }
      } catch (ex) {
        customerValid.current = false;
      } finally {
        setCustomerValidated(true);
      }
    }; 

    validateCustomer();
  },[]);

  const uploadFile = async (fileData, imageType) => {
    const data = new FormData();
    data.append("file_upload_location", fileData);
    data.append("customer_phone_number", customerPhoneNumber.current);
    data.append("document_description", fileData.name);
    data.append("image_type", imageType);
    try {
      setFileUploadInProgress(true);
      var response = await uploadFileService(data);

      const val = await response.json();
      if (val?.id) {
        setFileUploadFailed(false);
        return true;
      } else {
        setFileUploadFailed(true);
        return false;
      }
    } catch (ex) {
      setFileUploadFailed(true);
      return false;
    } finally {
      setFileUploadInProgress(false);
    }
  };

  const onCaptureComplete = async (captureType, image) => {
    switch (captureType) {
      case CAPTURETYPE.IDFront:
        if (await uploadFile(image, 1)) setCurrentPageMode(CAPTURETYPE.IDBack);
        else setCurrentPageMode(CAPTURETYPE.LandingPage);
        break;
      case CAPTURETYPE.IDBack:
        if (await uploadFile(image, 2)) setCurrentPageMode(CAPTURETYPE.Selfie);
        else setCurrentPageMode(CAPTURETYPE.LandingPage);
        break;
      case CAPTURETYPE.Selfie:
        if (await uploadFile(image, 3)) {
          if (ssnRequired.current) setCurrentPageMode(CAPTURETYPE.CaptureSSN);
          else setCurrentPageMode(CAPTURETYPE.CaptureComplete);
        } else setCurrentPageMode(CAPTURETYPE.LandingPage);
        break;
      case CAPTURETYPE.CaptureSSN:
        setCurrentPageMode(CAPTURETYPE.CaptureComplete);
      default:
        break;
    }
  };

  const captureSSNPage = () => {
    return (
      <>
        <div className="container">
          <div className="row py-3">
            <div className="mx-auto text-center">
              <button type="button"
                className="verification-button-back"
                onClick={() => setCurrentPageMode(CAPTURETYPE.Selfie)}
              >
                <img src={require("../assets/img/chevron-left.svg").default}/>
              </button>
              <h1 className="py-2">Verify</h1>
              <div className="px-3 fs-4">
                <label className="form-label">
                  Type your SSN in the field
                </label>
                <input
                  type="text"
                  className="form-control mx-auto fs-5"
                  id="ssn"
                  placeholder="SSN"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="py-3">
          <div className="span2 d-flex justify-content-center py-2">
            <button 
                className="btn btn-block verification-button-auth" 
                onClick={() => setCurrentPageMode(CAPTURETYPE.CaptureComplete)}
            >
              <span className="text-uppercase pb-1 fs-5">Submit</span>
            </button>
          </div>
        </div>
      </>
    );
  };

  const processingPage = (message) => {
    return (
      <div className="container py-5">
        <div className="row ">
          <div className="col justify-content-center text-center">
            <h3>{message}</h3>
          </div>
        </div>
      </div>
    );
  };

  const noActionRequiredScreen = (message) => {
    return (
      <div className="container py-5">
        <div className="row ">
          <div className="col justify-content-center text-center py-5">
            <FontAwesomeIcon icon={faCheckCircle} size="2x" color="#ffc55f" />
          </div>
        </div>
        <div className="row ">
          <div className="col justify-content-center text-center">
            <h3>{message}</h3>
          </div>
        </div>
        <div className="row ">
          <div className="col justify-content-center text-center">
            You can close the browser now.
          </div>
        </div>
      </div>
    );
  };

  const pageComponent = () => {
    if (fileUploadFailed) {
      return processingPage("Error while uploading the files. Please try again later");
    } else {
      if (!customerValidated) {
        return processingPage("Performing validations. Please wait...");
      } else {
        if (customerValid.current) {
          switch (currentPageMode) {
            case CAPTURETYPE.LandingPage:
              return (
                <KYCLanding
                  authStarted={() => setCurrentPageMode(CAPTURETYPE.IDFront)}
                />
              );
            case CAPTURETYPE.IDFront:
              return (
                <IDCapture
                  uploadInProgress={fileUploadinProgress}
                  captureType={currentPageMode}
                  changePage={(pageNum) => setCurrentPageMode(pageNum)}
                  onImageCapture={async (image) =>
                    await onCaptureComplete(CAPTURETYPE.IDFront, image)
                  }
                />
              );
            case CAPTURETYPE.IDBack:
              return (
                <IDCapture
                  uploadInProgress={fileUploadinProgress}
                  captureType={currentPageMode}
                  changePage={(pageNum) => setCurrentPageMode(pageNum)}
                  onImageCapture={async (image) =>
                    await onCaptureComplete(CAPTURETYPE.IDBack, image)
                  }
                />
              );
            case CAPTURETYPE.Selfie:
              return (
                <IDCapture
                  uploadInProgress={fileUploadinProgress}
                  captureType={currentPageMode}
                  changePage={(pageNum) => setCurrentPageMode(pageNum)}
                  onImageCapture={async (image) =>
                    await onCaptureComplete(CAPTURETYPE.Selfie, image)
                  }
                />
              );
            case CAPTURETYPE.CaptureSSN:
              return captureSSNPage();
            case CAPTURETYPE.CaptureComplete:
              return noActionRequiredScreen("Capture Complete!");
            default:
              break;
          }
        } else {
          return noActionRequiredScreen("No action required at this time!");
        }
      }
    }
  };

  return (
    <MobileOnly>
      <div className="mobile-landing-container">
        {fileUploadinProgress && (
          <Box sx={{ width: "100%" }}>
            <LinearProgress />
          </Box>
        )}
        {pageComponent()}
      </div>
    </MobileOnly>
  );
}

export default MobileVerificationLandingPage;
